const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_SITE_ORIGIN = "https://www.curatorwatchlist.com";
const PROFILE_RECENT_LIST_KEY = "account-seen-recent";
const PROFILE_TOP_RATED_LIST_KEY = "account-seen-top-user";
const CACHED_CURATOR_USERNAME_KEY = "curator:curatorProfileUsername";

function cleanUsername(value) {
  return String(value || "")
    .trim()
    .replace(/^@+/, "")
    .toLowerCase()
    .replace(/[^a-z0-9_.-]/g, "");
}

function readCachedCuratorUsername() {
  if (typeof window === "undefined" || !window.localStorage) return "";
  try {
    return cleanUsername(window.localStorage.getItem(CACHED_CURATOR_USERNAME_KEY) || "");
  } catch (_err) {
    return "";
  }
}

function writeCachedCuratorUsername(username) {
  const clean = cleanUsername(username);
  if (!clean || typeof window === "undefined" || !window.localStorage) return clean;
  try {
    window.localStorage.setItem(CACHED_CURATOR_USERNAME_KEY, clean);
  } catch (_err) {
    // ignore quota / private mode
  }
  return clean;
}

function buildCuratorFilmsPath(username, page = 1) {
  const clean = cleanUsername(username);
  const safePage = Math.max(1, parseInt(String(page || 1), 10) || 1);
  if (!clean) return "/";
  if (safePage <= 1) return `/${clean}/films/`;
  return `/${clean}/films/page/${safePage}/`;
}

function buildCuratorFilmsAiTextPath(username) {
  const clean = cleanUsername(username);
  return `/${clean}/films.txt`;
}

function resolveCuratorProfilePath({ profile, user, fallbackPath = "/profile", useCache = true } = {}) {
  // Prefer Curator profile username (authoritative) and cache it for top-bar links.
  const fromProfile = cleanUsername(profile?.username || profile?.curator_username);
  if (fromProfile) {
    writeCachedCuratorUsername(fromProfile);
    return buildCuratorFilmsPath(fromProfile);
  }
  // Auth0 nickname is a guess — use it for navigation but do not cache it.
  const fromUser = cleanUsername(
    user?.username
    || user?.nickname
    || user?.preferred_username
  );
  if (fromUser) return buildCuratorFilmsPath(fromUser);
  const cached = useCache ? readCachedCuratorUsername() : "";
  if (cached) return buildCuratorFilmsPath(cached);
  return fallbackPath;
}

function buildProfileListPath(username, listKey) {
  const clean = cleanUsername(username);
  const suffix = clean ? `?user=${encodeURIComponent(clean)}` : "";
  return `/list/${listKey}${suffix}`;
}

function titleFromSlug(value) {
  const cleaned = String(value || "")
    .trim()
    .replace(/-\d{4}$/, "")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";
  return cleaned.replace(/\b[a-z]/g, (char) => char.toUpperCase());
}

function resolveFilmYear(row) {
  const year = Number(row?.year || row?.Year || row?.releaseYear || 0);
  if (Number.isFinite(year) && year > 1800) return year;
  const date = String(row?.releaseDate || row?.release_date || row?.first_air_date || "").trim();
  const parsed = Number(date.slice(0, 4));
  if (Number.isFinite(parsed) && parsed > 1800) return parsed;
  const slugYear = String(row?.filmSlug || row?.slug || "").match(/(?:^|-)(\d{4})$/);
  const parsedSlugYear = Number(slugYear?.[1] || 0);
  return Number.isFinite(parsedSlugYear) && parsedSlugYear > 1800 ? parsedSlugYear : null;
}

function normalizeCuratorFilm(row, index = 0) {
  const fallbackTitle = titleFromSlug(row?.filmSlug || row?.slug);
  const title = String(row?.title || row?.name || row?.Title || fallbackTitle || "Untitled").trim() || "Untitled";
  const rating = Number(row?.rating ?? row?.userScore ?? 0);
  const tmdbId = Number(row?.tmdbId || row?.tmdb_id || 0);
  return {
    title,
    year: resolveFilmYear(row),
    rating: Number.isFinite(rating) && rating > 0 ? rating : null,
    tmdbId: Number.isFinite(tmdbId) && tmdbId > 0 ? tmdbId : null,
    filmSlug: String(row?.filmSlug || row?.slug || "").trim(),
    filmId: String(row?.filmId || row?.id || "").trim(),
    index,
  };
}

function normalizeUserScore100(row) {
  const value = Number(row?.userScore ?? row?.rating);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value <= 5 ? Math.round(value * 20) : Math.round(Math.min(value, 100));
}

function resolveReleaseDate(row) {
  const raw = String(row?.releaseDate || row?.release_date || row?.first_air_date || row?.date || "").trim();
  if (raw) return raw;
  const year = resolveFilmYear(row);
  return year ? String(year) : "";
}

function normalizeCuratorProfileListItem(row, index = 0) {
  const film = normalizeCuratorFilm(row, index);
  const releaseDate = resolveReleaseDate(row);
  const hasYearOnlyRelease = releaseDate ? /^\d{4}$/.test(releaseDate) : false;
  const releaseMs = hasYearOnlyRelease
    ? (Date.parse(`${releaseDate.slice(0, 4)}-01-01`) || 0)
    : (releaseDate ? (Date.parse(releaseDate) || 0) : 0);
  const poster = String(row?.poster || row?.posterUrl || row?.poster_path || row?.posterPath || "").trim();
  const backdrop = String(row?.backdrop || row?.backdropUrl || row?.backdrop_path || row?.backdropPath || "").trim();
  const watchedRaw = String(row?.watchedDate || row?.watched_at || row?.diaryDate || row?.date || "").trim();
  const watchedMs = watchedRaw ? (Date.parse(watchedRaw) || 0) : 0;
  return {
    id: `profile-${film.tmdbId || film.filmSlug || film.filmId || film.title}-${index}`,
    title: film.title,
    mediaType: /tv|series|show/.test(String(row?.mediaType || row?.titleType || "")) ? "tv" : "movie",
    tmdbId: film.tmdbId,
    poster,
    backdrop,
    releaseDate,
    releaseMs,
    releaseYear: film.year || Number(String(releaseDate || "").slice(0, 4)) || null,
    score: null,
    ratingsCount: 0,
    watchSources: [],
    inTheaters: false,
    userScore: normalizeUserScore100(row),
    filmSlug: film.filmSlug,
    watchedMs,
    logIndex: index,
  };
}

function sortRecentlyWatchedItems(a, b) {
  // Letterboxd /films/by/date/: preserve scrape order (most recently logged first).
  return Number(a?.logIndex || 0) - Number(b?.logIndex || 0);
}

function sortByEntryRatingItems(a, b) {
  // Letterboxd /films/by/entry-rating/: highest user score first, then by/date order.
  const scoreDelta = Number(b?.userScore || 0) - Number(a?.userScore || 0);
  if (scoreDelta !== 0) return scoreDelta;
  return Number(a?.logIndex || 0) - Number(b?.logIndex || 0);
}

function buildCuratorProfileShelfRows(username, films = [], { displayName = "" } = {}) {
  const items = (Array.isArray(films) ? films : [])
    .map(normalizeCuratorProfileListItem)
    .filter((item) => item.title);
  const recent = items.slice().sort(sortRecentlyWatchedItems);
  const topRated = items
    .filter((item) => item.userScore != null && Number.isFinite(Number(item.userScore)) && Number(item.userScore) > 0)
    .slice()
    .sort(sortByEntryRatingItems);
  const possessiveName = String(displayName || "").trim();
  return [
    {
      key: PROFILE_RECENT_LIST_KEY,
      label: "Recently Watched",
      listPath: buildProfileListPath(username, PROFILE_RECENT_LIST_KEY),
      items: recent,
      status: "ready",
    },
    {
      key: PROFILE_TOP_RATED_LIST_KEY,
      label: possessiveName ? `Top Rated by ${possessiveName}` : "Top Rated",
      listPath: buildProfileListPath(username, PROFILE_TOP_RATED_LIST_KEY),
      items: topRated,
      status: "ready",
    },
  ].filter((row) => row.items.length);
}

function paginateCuratorFilms(films, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  const rows = (Array.isArray(films) ? films : []).map(normalizeCuratorFilm);
  const size = Math.max(1, parseInt(String(pageSize || DEFAULT_PAGE_SIZE), 10) || DEFAULT_PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(rows.length / size));
  const requestedPage = Math.max(1, parseInt(String(page || 1), 10) || 1);
  const safePage = Math.min(requestedPage, totalPages);
  const start = (safePage - 1) * size;
  return {
    page: safePage,
    pageSize: size,
    totalPages,
    totalItems: rows.length,
    items: rows.slice(start, start + size),
  };
}

function formatRating(rating) {
  const value = Number(rating);
  if (!Number.isFinite(value) || value <= 0) return "unrated";
  const stars = value > 5 ? Math.min(value, 100) / 20 : value;
  const rounded = Math.round(stars * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}/5` : `${rounded.toFixed(1).replace(/\.0$/, "")}/5`;
}

function filmAiLine(film, displayIndex) {
  const year = film.year ? ` (${film.year})` : "";
  const ids = [
    film.tmdbId ? `tmdb:${film.tmdbId}` : null,
    film.filmSlug ? `letterboxd:${film.filmSlug}` : null,
  ].filter(Boolean);
  const idSuffix = ids.length ? ` - ${ids.join(" - ")}` : "";
  return `${displayIndex}. ${film.title}${year} - user rating: ${formatRating(film.rating)}${idSuffix}`;
}

function buildCuratorFilmsAiText({
  profile,
  letterboxdProfile,
  films,
  page = 1,
  totalPages = 1,
} = {}) {
  const username = cleanUsername(profile?.username || letterboxdProfile?.letterboxdUsername);
  const displayName = String(profile?.displayName || letterboxdProfile?.profile?.displayName || username || "Curator user").trim();
  const letterboxdUsername = cleanUsername(letterboxdProfile?.letterboxdUsername || letterboxdProfile?.profile?.username);
  const watchedTotal = Number(letterboxdProfile?.watched?.total ?? films?.length ?? 0);
  const fetchedAt = letterboxdProfile?.fetchedAt ? new Date(letterboxdProfile.fetchedAt) : null;
  const fetchedLine = fetchedAt && !Number.isNaN(fetchedAt.getTime())
    ? `Fetched: ${fetchedAt.toISOString()}`
    : "";
  const safePage = Math.max(1, parseInt(String(page || 1), 10) || 1);
  const safeTotalPages = Math.max(1, parseInt(String(totalPages || 1), 10) || 1);
  const normalizedFilms = (Array.isArray(films) ? films : []).map(normalizeCuratorFilm);
  const startIndex = (safePage - 1) * DEFAULT_PAGE_SIZE;

  return [
    `Curator profile: ${displayName}${username ? ` (@${username})` : ""}`,
    letterboxdUsername ? `Letterboxd username: @${letterboxdUsername}` : "",
    letterboxdUsername && username && letterboxdUsername !== username
      ? `Note: Curator @${username} is linked to Letterboxd @${letterboxdUsername}`
      : "",
    `Watched films: ${Number.isFinite(watchedTotal) ? watchedTotal : normalizedFilms.length}`,
    `Page: ${safePage} of ${safeTotalPages}`,
    fetchedLine,
    "",
    "Readable film list for AI recommendations:",
    ...normalizedFilms.map((film, index) => filmAiLine(film, startIndex + index + 1)),
  ].filter((line) => line !== "").join("\n");
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildCuratorFilmsPageHtml({
  profile,
  letterboxdProfile,
  pageData,
  siteOrigin = DEFAULT_SITE_ORIGIN,
} = {}) {
  const username = cleanUsername(profile?.username || letterboxdProfile?.letterboxdUsername);
  const letterboxdUsername = cleanUsername(letterboxdProfile?.letterboxdUsername || letterboxdProfile?.profile?.username);
  const displayName = String(profile?.displayName || letterboxdProfile?.profile?.displayName || username || "Curator user").trim();
  const path = buildCuratorFilmsPath(username, pageData?.page || 1);
  const canonicalUrl = `${String(siteOrigin || DEFAULT_SITE_ORIGIN).replace(/\/$/, "")}${path}`;
  const title = `${displayName}'s films - Curator`;
  const description = `${displayName}'s watched and rated films on Curator, formatted for movie recommendations.`;
  const aiText = buildCuratorFilmsAiText({
    profile,
    letterboxdProfile,
    films: pageData?.items || [],
    page: pageData?.page || 1,
    totalPages: pageData?.totalPages || 1,
  });
  const rows = (pageData?.items || [])
    .map((film, index) => `<li>${escapeHtml(filmAiLine(film, ((pageData.page - 1) * pageData.pageSize) + index + 1))}</li>`)
    .join("\n");

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(title)}</title>
  <meta name="description" content="${escapeHtml(description)}" />
  <meta property="og:type" content="profile" />
  <meta property="og:url" content="${escapeHtml(canonicalUrl)}" />
  <meta property="og:title" content="${escapeHtml(title)}" />
  <meta property="og:description" content="${escapeHtml(description)}" />
  <link rel="canonical" href="${escapeHtml(canonicalUrl)}" />
</head>
<body>
  <main style="max-width:920px;margin:0 auto;padding:24px 16px;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif;">
    <h1>${escapeHtml(displayName)}'s films</h1>
    <p>@${escapeHtml(username)}${letterboxdUsername && letterboxdUsername !== username ? ` · Letterboxd @${escapeHtml(letterboxdUsername)}` : ""} · ${escapeHtml(String(pageData?.totalItems || 0))} watched films · page ${escapeHtml(String(pageData?.page || 1))} of ${escapeHtml(String(pageData?.totalPages || 1))}</p>
    <section aria-label="AI readable profile summary">
      <h2>AI-readable profile</h2>
      <pre style="white-space:pre-wrap;line-height:1.45;background:#f6f6f6;padding:16px;border-radius:12px;">${escapeHtml(aiText)}</pre>
    </section>
    <section aria-label="Watched and rated films">
      <h2>Watched and rated films</h2>
      <ol>${rows}</ol>
    </section>
  </main>
</body>
</html>`;
}

module.exports = {
  DEFAULT_PAGE_SIZE,
  CACHED_CURATOR_USERNAME_KEY,
  cleanUsername,
  readCachedCuratorUsername,
  writeCachedCuratorUsername,
  buildCuratorFilmsPath,
  buildCuratorFilmsAiTextPath,
  buildCuratorProfileShelfRows,
  resolveCuratorProfilePath,
  normalizeCuratorFilm,
  normalizeCuratorProfileListItem,
  paginateCuratorFilms,
  buildCuratorFilmsAiText,
  buildCuratorFilmsPageHtml,
};
